// Sheet de configuración de la INFOGRAFÍA (modo Catálogo de muebles).
//
// Se abre desde la card global "Infografía" de la pantalla "Revisá tu
// catálogo". Las opciones son GLOBALES de la tanda, no por producto: son
// decisiones de estilo ("quiero las infografías con medidas o sin"), no algo
// que cambie de un mueble a otro. Si algún día hace falta el override por
// producto, se agrega sin romper esto.
//
// Qué hacen los toggles: NO filtran nada del lado del front. Viajan como dos
// booleanos en el payload (`mostrar_medidas` / `mostrar_atributos`) y el prompt
// de infografía en n8n arma o no arma el bloque correspondiente. Mismo
// mecanismo que ya usa `instruccion_adicional` en todos los prompts de Atlas.
//
// Mobile: bottom sheet con drag handle. Desktop: panel lateral derecho.
// Estructura calcada de `VideoConfigSheet` para no inventar un layout nuevo.

import React from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Plus, Ruler, Sparkles, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';

/** Slugs que viajan en `estilo_infografia`. Tienen que coincidir EXACTO con
 *  los que evalúa el ternario del prompt `prompt text (infografia)` en n8n. */
export type EstiloInfografia =
  | 'fotografico'
  | 'boceto_tecnico'
  | 'boceto_linea';

export const ESTILOS_INFOGRAFIA: {
  id: EstiloInfografia;
  nombre: string;
  descripcion: string;
  /** Miniatura de ejemplo del estilo (en `public/images/infografia_estilos/`). */
  preview: string;
}[] = [
  {
    id: 'fotografico',
    nombre: 'Fotográfico',
    descripcion: 'El producto como foto, sobre fondo claro, con etiquetas.',
    preview: '/images/infografia_estilos/fotografico.png',
  },
  {
    id: 'boceto_tecnico',
    nombre: 'Boceto técnico',
    descripcion: 'Dibujo a lápiz sobre papel, con las cotas a mano alzada.',
    preview: '/images/infografia_estilos/boceto_tecnico.png',
  },
  {
    id: 'boceto_linea',
    nombre: 'Boceto a línea',
    descripcion: 'Ilustración a tinta, trazo suelto sobre fondo blanco.',
    preview: '/images/infografia_estilos/boceto_linea.png',
  },
];

/** Slugs que viajan en `color_infografia`. Tienen que coincidir EXACTO con el
 *  ternario del prompt `prompt text (infografia)` en n8n, que define el fondo
 *  de la lámina (reemplaza el beige que estaba hardcodeado). */
export type ColorInfografia = 'blanco' | 'beige' | 'grafito';

export const COLORES_INFOGRAFIA: {
  id: ColorInfografia;
  nombre: string;
  /** Muestra para el selector. NO es exactamente lo que dibuja la IA: el color
   *  real lo define el prompt de n8n; esto es solo la previsualización. */
  swatch: string;
}[] = [
  { id: 'blanco', nombre: 'Blanco', swatch: '#FFFFFF' },
  { id: 'beige', nombre: 'Beige', swatch: '#EFE9E1' },
  { id: 'grafito', nombre: 'Grafito', swatch: '#3A3A3C' },
];

export interface InfografiaConfig {
  /** Estilo visual de la lámina. */
  estilo: EstiloInfografia;
  /** Color de fondo de la lámina. */
  color: ColorInfografia;
  /** Dibujar las cotas con las medidas cargadas. */
  mostrarMedidas: boolean;
  /** Agregar las "pills" de características que la IA deduce de la foto. */
  mostrarAtributos: boolean;
}

export const INFOGRAFIA_CONFIG_DEFAULT: InfografiaConfig = {
  estilo: 'fotografico',
  // Fondo blanco por default: es el look de ficha limpia de la referencia que
  // pidió Imanol (2026-07-22). Beige (el que estaba) y grafito son alternativas.
  color: 'blanco',
  // Los dos toggles arrancan en ON: es lo que la infografía promete por
  // defecto, y el usuario apaga lo que no quiera (reunión del 2026-07-21).
  mostrarMedidas: true,
  mostrarAtributos: true,
};

export interface InfografiaConfigSheetProps {
  open: boolean;
  /** En variante 'global': la config de la tanda. En 'producto': la config
   *  EFECTIVA del producto (global ⊕ override), para que los controles muestren
   *  lo que realmente se va a generar. */
  config: InfografiaConfig;
  onChange: (next: InfografiaConfig) => void;
  onClose: () => void;
  /** true si NINGÚN producto de la tanda tiene medidas cargadas — se usa para
   *  avisar que el toggle de medidas no va a tener nada que dibujar. */
  sinMedidasCargadas?: boolean;
  /** Si es false, se OCULTA toda la sección "Mostrar medidas" (categorías que no se
   *  venden por tamaño: herramienta/decoración/iluminación). En variante 'global' va
   *  false solo si NINGÚN producto de la tanda admite medidas. Default true (back-compat). */
  admiteMedidas?: boolean;
  // ── Modo "por producto" (override) ───────────────────────────────────────
  /** 'global' = la card de arriba (default de la tanda). 'producto' = el
   *  override de un producto puntual, con el switch "Personalizar". */
  variante?: 'global' | 'producto';
  /** Nombre del producto, para el header en variante 'producto'. */
  productoNombre?: string;
  /** En 'producto': si el producto está personalizado (tiene override propio).
   *  Cuando es false, los controles se ven en gris (usa el global). */
  personalizado?: boolean;
  /** Prender/apagar la personalización de este producto. */
  onTogglePersonalizado?: (v: boolean) => void;
  // ── Características curadas (por producto) ────────────────────────────────
  /** Lista de características a mostrar (texto libre). Hoy manual; con el
   *  backend nuevo vendrán pre-cargadas por la IA. Ver ADR-808. */
  atributos?: string[];
  onAtributosChange?: (next: string[]) => void;
  /** Tope de características (default 3). */
  maxAtributos?: number;
}

/** Fila con título, descripción y switch. Mismo markup que VideoConfigSheet. */
const FilaToggle: React.FC<{
  id: string;
  icono: React.ReactNode;
  titulo: string;
  descripcion: string;
  valor: boolean;
  onChange: (v: boolean) => void;
  nota?: string;
}> = ({ id, icono, titulo, descripcion, valor, onChange, nota }) => (
  <div className="rounded-2xl border border-neutral-200 p-3.5 flex items-start justify-between gap-3">
    <div className="min-w-0">
      <p
        id={id}
        className="text-[14px] font-semibold text-brand-black/90 inline-flex items-center gap-1.5"
      >
        {icono}
        {titulo}
      </p>
      <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5 leading-snug">
        {descripcion}
      </p>
      {nota && (
        <p className="text-[12px] text-warning-text mt-1 leading-snug">{nota}</p>
      )}
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={valor}
      aria-labelledby={id}
      onClick={() => onChange(!valor)}
      className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary/30 ${
        valor ? 'bg-brand-primary' : 'bg-neutral-300'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          valor ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  </div>
);

export const InfografiaConfigSheet: React.FC<InfografiaConfigSheetProps> = ({
  open,
  config,
  onChange,
  onClose,
  sinMedidasCargadas = false,
  admiteMedidas = true,
  variante = 'global',
  productoNombre,
  personalizado = false,
  onTogglePersonalizado,
  atributos,
  onAtributosChange,
  maxAtributos = 4,
}) => {
  const { mounted, animated } = useExitTransition(open);
  const esProducto = variante === 'producto';
  // Los controles se editan cuando es la card global, o cuando el producto está
  // personalizado. Si no, se muestran en gris (heredando el global).
  const controlesActivos = !esProducto || personalizado;
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>

        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity md:hidden"
          >
            Cerrar
          </button>
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 md:flex-1 truncate">
            {esProducto ? 'Personalizar infografía' : 'Configurar infografía'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        <div className="px-5 py-4 flex flex-col gap-3 overflow-y-auto">
          <p className="text-[13px] text-[#3C3C43]/60 leading-snug">
            {esProducto
              ? personalizado
                ? `Estás personalizando la infografía de ${productoNombre ?? 'este producto'}. Lo que no toques, hereda la configuración general.`
                : `${productoNombre ?? 'Este producto'} usa la configuración general. Activá para darle la suya.`
              : 'Aplica a todas las infografías de esta tanda.'}
          </p>

          {esProducto && (
            <FilaToggle
              id="infografia-personalizar"
              icono={<Pencil className="w-3.5 h-3.5 text-[#3C3C43]/50" />}
              titulo="Personalizar este producto"
              descripcion="Si está apagado, usa la configuración general de la tanda."
              valor={!!personalizado}
              onChange={(v) => onTogglePersonalizado?.(v)}
            />
          )}

          <div
            className={`flex flex-col gap-3 transition-opacity ${
              controlesActivos ? '' : 'opacity-40 pointer-events-none select-none'
            }`}
            aria-disabled={!controlesActivos}
          >
          <div>
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
              Estilo
            </p>
            <div className="grid grid-cols-2 gap-2">
              {ESTILOS_INFOGRAFIA.map((e) => {
                const sel = config.estilo === e.id;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => onChange({ ...config, estilo: e.id })}
                    className={`w-full text-left rounded-2xl border overflow-hidden transition-all active:scale-[0.99] ${
                      sel
                        ? 'border-brand-primary ring-2 ring-brand-primary/20'
                        : 'border-neutral-200 hover:border-brand-primary/40'
                    }`}
                  >
                    <div className="w-full aspect-[4/3] bg-neutral-100 overflow-hidden">
                      <img
                        src={e.preview}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div
                      className={`px-3 py-2.5 text-center ${sel ? 'bg-brand-primary/[0.03]' : 'bg-white'}`}
                    >
                      <p className="text-[13.5px] font-semibold text-brand-black/90">
                        {e.nombre}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* El color solo aplica al estilo fotográfico. En los bocetos el medio
              define la superficie (papel / blanco), así que el selector se
              esconde para no ofrecer un control que no hace nada. */}
          {config.estilo === 'fotografico' && (
          <div>
            <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
              Color de fondo
            </p>
            <div className="flex gap-2">
              {COLORES_INFOGRAFIA.map((c) => {
                const sel = config.color === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onChange({ ...config, color: c.id })}
                    className={`flex-1 rounded-2xl border p-3 flex flex-col items-center gap-2 transition-all active:scale-[0.99] ${
                      sel
                        ? 'border-brand-primary ring-2 ring-brand-primary/20 bg-brand-primary/[0.03]'
                        : 'border-neutral-200 bg-white hover:border-brand-primary/40'
                    }`}
                  >
                    <span
                      className="w-7 h-7 rounded-full border border-black/10"
                      style={{ backgroundColor: c.swatch }}
                      aria-hidden
                    />
                    <span className="text-[12.5px] font-medium text-brand-black/90">
                      {c.nombre}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Sección de medidas: se oculta para categorías que no se venden por
              tamaño (herramienta/decoración/iluminación). En la global va según si
              ALGÚN producto de la tanda admite medidas. */}
          {admiteMedidas && (
            <FilaToggle
              id="infografia-medidas"
              icono={<Ruler className="w-3.5 h-3.5 text-[#3C3C43]/50" />}
              titulo="Mostrar medidas"
              descripcion="Dibuja las líneas de cota con el alto, el ancho y la profundidad que cargaste en cada producto."
              valor={config.mostrarMedidas}
              onChange={(v) => onChange({ ...config, mostrarMedidas: v })}
              nota={
                config.mostrarMedidas && sinMedidasCargadas
                  ? 'Ninguno de tus productos tiene medidas cargadas todavía, así que no se van a dibujar cotas.'
                  : undefined
              }
            />
          )}

          <FilaToggle
            id="infografia-atributos"
            icono={<Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/50" />}
            titulo="Mostrar características"
            descripcion="Agrega etiquetas con detalles del producto (por ejemplo “2 cajones” o “patas de madera”)."
            valor={config.mostrarAtributos}
            onChange={(v) => onChange({ ...config, mostrarAtributos: v })}
            nota={
              config.mostrarAtributos
                ? 'Las deduce la IA mirando la foto, así que revisalas antes de publicar.'
                : undefined
            }
          />
          </div>

          {/* Características curadas — por producto, siempre editable (no depende
              del switch "Personalizar"). Aparece cuando se muestran atributos. */}
          {esProducto && config.mostrarAtributos && (
            <div>
              <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-2">
                Características (máx {maxAtributos})
              </p>
              <div className="flex flex-col gap-2">
                {(atributos ?? []).map((attr, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={attr}
                      maxLength={24}
                      placeholder="Ej: Roble macizo"
                      onChange={(e) => {
                        const next = [...(atributos ?? [])];
                        next[i] = e.target.value;
                        onAtributosChange?.(next);
                      }}
                      className="flex-1 min-w-0 rounded-xl border border-neutral-200 px-3 py-2 text-[13.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 focus:outline-none focus:border-brand-primary/50"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        onAtributosChange?.(
                          (atributos ?? []).filter((_, j) => j !== i),
                        )
                      }
                      aria-label="Quitar característica"
                      className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] hover:text-brand-black/70 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {(atributos?.length ?? 0) < maxAtributos && (
                <button
                  type="button"
                  onClick={() => onAtributosChange?.([...(atributos ?? []), ''])}
                  className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-primary hover:opacity-70 transition-opacity"
                >
                  <Plus className="w-4 h-4" />
                  Agregar característica
                </button>
              )}
              <p className="text-[12px] text-[#3C3C43]/50 mt-2 leading-snug">
                La IA sugiere hasta {maxAtributos} al generar; acá las editás,
                borrás o agregás a mano.
              </p>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default InfografiaConfigSheet;
